import React, { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, FileText, Phone, Shield, Truck, Wrench, ChevronLeft, ChevronRight, X, Eye, Download, BookOpen, Maximize2, Minimize2 } from 'lucide-react';
import { fbEvents } from '@/lib/facebook-pixel';
import { getPublicApiUrl } from '@/lib/publicApi';

const API_URL = getPublicApiUrl();

export default function PublicMachineDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useOutletContext() || {};
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [brochureExpanded, setBrochureExpanded] = useState(false);

  useEffect(() => {
    fetchMachine();
  }, [id]);

  // Track ViewContent when machine is loaded
  useEffect(() => {
    if (machine) {
      fbEvents.viewMachine(machine);
    }
  }, [machine]);

  const fetchMachine = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/machines/${id}`);
      if (!res.ok) throw new Error('Machine not found');
      const data = await res.json();
      setMachine(data);
    } catch (error) {
      console.error('Failed to fetch machine:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return url;
  };

  // Combine main image with gallery images
  const allImages = machine ? [
    machine.image_url,
    ...(machine.gallery_images || [])
  ].filter(Boolean) : [];

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-xl text-slate-500 mb-4">Machine non trouvée</p>
        <Link to="/portail/machines" className="text-yellow-600 hover:text-yellow-700">
          ← Retour aux machines
        </Link>
      </div>
    );
  }

  const specs = [
    { label: 'Poids', value: machine.weight },
    { label: 'Puissance', value: machine.power },
    { label: 'Longueur', value: machine.longueur },
    { label: 'Largeur', value: machine.largeur },
    { label: 'Profondeur de fouille', value: machine.profondeur_fouille },
    { label: 'Hauteur de chargement', value: machine.hauteur_chargement },
    { label: 'Moteur', value: machine.marque_moteur },
    { label: 'Puissance moteur', value: machine.puissance_moteur },
  ].filter(s => s.value);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/portail" className="hover:text-yellow-600">Accueil</Link>
            <span>/</span>
            <Link to="/portail/machines" className="hover:text-yellow-600">Machines</Link>
            <span>/</span>
            <span className="text-slate-800">{machine.name}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              {/* Main Image with Navigation */}
              <div className="relative aspect-[4/3] bg-white rounded-2xl overflow-hidden shadow-lg group">
                {allImages.length > 0 && getImageUrl(allImages[selectedImageIndex]) ? (
                  <>
                    <img
                      src={getImageUrl(allImages[selectedImageIndex])}
                      alt={`${machine.name} - Image ${selectedImageIndex + 1}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setLightboxOpen(true)}
                    />
                    {/* Zoom indicator */}
                    <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Eye className="w-4 h-4" />
                      Agrandir
                    </div>
                    {/* Navigation arrows */}
                    {allImages.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prevImage(); }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                        >
                          <ChevronLeft className="w-6 h-6 text-slate-700" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); nextImage(); }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                        >
                          <ChevronRight className="w-6 h-6 text-slate-700" />
                        </button>
                      </>
                    )}
                    {/* Image counter */}
                    {allImages.length > 1 && (
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-1.5 rounded-full text-sm">
                        {selectedImageIndex + 1} / {allImages.length}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <Wrench className="w-24 h-24" />
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {allImages.length > 1 && (
                <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                        index === selectedImageIndex
                          ? 'border-yellow-500 shadow-md'
                          : 'border-transparent hover:border-slate-300'
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${machine.name} - Miniature ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {machine.brand && (
                <span className="text-sm font-medium text-yellow-600 uppercase tracking-wider">{machine.brand}</span>
              )}
              <h1 className="text-4xl font-bold text-slate-800 mt-2 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                {machine.name}
              </h1>
              {machine.model && (
                <p className="text-xl text-slate-500 mb-4">{machine.model}</p>
              )}
              
              {machine.warranty && (
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  Garantie: {machine.warranty}
                </div>
              )}

              {/* Description */}
              {(machine.website_description || machine.description) && (
                <div className="mb-8">
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {machine.website_description || machine.description}
                  </p>
                </div>
              )}

              {/* Highlights */}
              {machine.website_highlights && machine.website_highlights.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-800 mb-3">Points forts :</h3>
                  <ul className="space-y-2">
                    {machine.website_highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-600">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={() => navigate(`/portail/configurateur?machine=${machine.id}`)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition shadow-lg shadow-yellow-500/25"
                >
                  Demander un prix
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a
                  href={`tel:${settings?.contact_phone || '0693526449'}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition"
                >
                  <Phone className="w-5 h-5" />
                  Nous appeler
                </a>
              </div>

              {/* Demo Button */}
              <button
                onClick={() => navigate(`/portail/configurateur?machine=${machine.id}&demo=true`)}
                className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition"
              >
                <span>🎯</span>
                Souhaitez-vous découvrir cette machine ?
              </button>
            </div>
          </div>

          {/* Specifications */}
          {specs.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-slate-800 mb-8" style={{ fontFamily: 'Barlow Condensed' }}>
                Caractéristiques Techniques
              </h2>
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="grid md:grid-cols-2 lg:grid-cols-4">
                  {specs.map((spec, index) => (
                    <div 
                      key={index}
                      className={`p-6 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b md:border-b-0 md:border-r last:border-0`}
                    >
                      <p className="text-sm text-slate-500 mb-1">{spec.label}</p>
                      <p className="text-lg font-semibold text-slate-800">{spec.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Brochure Section - Integrated Viewer */}
          {machine.brochure_url && (
            <div className="mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                  <BookOpen className="w-6 h-6 inline-block mr-2 text-yellow-500" />
                  Documentation Technique
                </h2>
                <div className="flex items-center gap-2">
                  {machine.brochure_downloadable !== false && (
                    <a
                      href={getImageUrl(machine.brochure_url)}
                      download
                      className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger
                    </a>
                  )}
                  <button
                    onClick={() => setBrochureExpanded(!brochureExpanded)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition"
                  >
                    {brochureExpanded ? (
                      <>
                        <Minimize2 className="w-4 h-4" />
                        Réduire
                      </>
                    ) : (
                      <>
                        <Maximize2 className="w-4 h-4" />
                        Agrandir
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {/* PDF Viewer */}
              <div className={`bg-white rounded-2xl shadow-sm overflow-hidden transition-all duration-300 ${brochureExpanded ? 'h-[80vh]' : 'h-[500px]'}`}>
                <iframe
                  src={`${getImageUrl(machine.brochure_url)}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-full h-full border-0"
                  title={`Brochure ${machine.name}`}
                />
              </div>
              
              {/* Mobile fallback - some browsers don't support inline PDF */}
              <div className="mt-4 text-center text-sm text-slate-500">
                <p>
                  Le PDF ne s'affiche pas correctement ?{' '}
                  <a 
                    href={getImageUrl(machine.brochure_url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-yellow-600 hover:text-yellow-700 underline"
                  >
                    Ouvrir dans un nouvel onglet
                  </a>
                </p>
              </div>
            </div>
          )}

          {/* Benefits */}
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Livraison incluse</h3>
              <p className="text-slate-600 text-sm">Livraison sur toute l'île de La Réunion</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Garantie constructeur</h3>
              <p className="text-slate-600 text-sm">Pièces et main d'œuvre</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">SAV local</h3>
              <p className="text-slate-600 text-sm">Assistance technique et pièces détachées</p>
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-12">
            <Link
              to="/portail/machines"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-yellow-600 transition"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour aux machines
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && allImages.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Navigation arrows */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition z-10"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition z-10"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}

          {/* Image */}
          <img
            src={getImageUrl(allImages[selectedImageIndex])}
            alt={`${machine.name} - Image ${selectedImageIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Image counter */}
          {allImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white px-6 py-2 rounded-full">
              {selectedImageIndex + 1} / {allImages.length}
            </div>
          )}

          {/* Thumbnail strip */}
          {allImages.length > 1 && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
              {allImages.map((img, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(index); }}
                  className={`w-12 h-12 rounded overflow-hidden border-2 transition ${
                    index === selectedImageIndex
                      ? 'border-yellow-500'
                      : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Miniature ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
